import type { MessageDescriptor } from '@lingui/core'
import { msg, t } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { Badge } from 'designSystem/components/Badge'
import { Button } from 'designSystem/components/Button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'designSystem/components/Dialog'
import { Input } from 'designSystem/components/Input'
import { Spinner } from 'designSystem/components/Spinner'
import { Text } from 'designSystem/components/Text'
import React, { useState } from 'react'
import styled from 'styled-components'

import {
  type FimPhotoData,
  type FimPhotoEntry,
  photoDisplayTitle,
  type PhotoStatus,
} from './types'

const PHOTO_STATUS_LABELS: Record<PhotoStatus, MessageDescriptor> = {
  pending_upload: msg`Pending upload`,
  unattached: msg`Unattached`,
  attached: msg`Attached`,
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  max-height: calc(90vh - 5rem);
  overflow-y: auto;
`

const FullImage = styled.img`
  max-width: 100%;
  max-height: 55vh;
  border-radius: 0.3rem;
  object-fit: contain;
  background: color-mix(in srgb, var(--foreground) 40%, transparent);
`

const CaptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`

const CaptionFieldSlot = styled.div`
  flex: 1 1 0;
  min-width: 0;
`

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
`

type ConfirmingAction = 'detach' | 'delete' | null

const formatWhen = (iso: string): string => new Date(iso).toLocaleString()

/** Entries older than capture metadata show only what they have. */
function provenanceSummary(data: FimPhotoData): string[] {
  const summary: string[] = []
  const uploader = data.uploaded_by?.name || data.uploaded_by?.email
  if (uploader && data.uploaded_at) {
    const uploadedAt = formatWhen(data.uploaded_at)
    summary.push(t`Uploaded by ${uploader} on ${uploadedAt}`)
  } else if (uploader) {
    summary.push(t`Uploaded by ${uploader}`)
  }
  const capturedAt = formatWhen(data.captured_at)
  summary.push(
    data.captured_at_source === 'exif'
      ? t`Captured ${capturedAt} (from the camera clock)`
      : t`Captured ${capturedAt} (at upload time)`
  )
  return summary
}

/** The action callbacks own error reporting; this only shows busy state. */
export const PhotoLightbox: React.FC<{
  entry: FimPhotoEntry
  pending: boolean
  pendingNote: string
  imageUrl: string | undefined
  canEdit: boolean
  onSaveCaption: (caption: string) => Promise<void>
  onDetach: () => Promise<void>
  onDelete: () => Promise<void>
  onClose: () => void
}> = ({
  entry,
  pending,
  pendingNote,
  imageUrl,
  canEdit,
  onSaveCaption,
  onDetach,
  onDelete,
  onClose,
}) => {
  const { t } = useLingui()
  const savedCaption = entry.data.caption ?? ''
  const [caption, setCaption] = useState(savedCaption)
  const [confirming, setConfirming] = useState<ConfirmingAction>(null)
  const [busy, setBusy] = useState(false)

  const title = photoDisplayTitle(entry)
  const captionDirty = caption !== savedCaption

  const run = (action: () => Promise<void>) => {
    setBusy(true)
    action().finally(() => {
      setBusy(false)
      setConfirming(null)
    })
  }

  const onOpenChange = (open: boolean) => {
    if (!open) onClose()
  }
  const onCaptionInput = (event: React.ChangeEvent<HTMLInputElement>) =>
    setCaption(event.target.value)
  const onSave = () => run(() => onSaveCaption(caption))
  const onConfirm = () => run(confirming === 'delete' ? onDelete : onDetach)
  const onConfirmDetach = () => setConfirming('detach')
  const onConfirmDelete = () => setConfirming('delete')
  const onCancelConfirm = () => setConfirming(null)

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle size='sm'>{title}</DialogTitle>
        </DialogHeader>
        <Body>
          {imageUrl ? (
            <FullImage src={imageUrl} alt={title} />
          ) : (
            <Text color='muted' variant='caption'>
              <Trans>Loading full-resolution photo…</Trans>
            </Text>
          )}
          <ChipRow>
            <Badge variant='outlineMuted'>
              {t(PHOTO_STATUS_LABELS[entry.data.status])}
            </Badge>
          </ChipRow>
          {pending && (
            <Text color='muted' variant='caption'>
              {pendingNote}
            </Text>
          )}
          {provenanceSummary(entry.data).map((line) => (
            <Text key={line} color='muted' variant='caption'>
              {line}
            </Text>
          ))}
          {canEdit && (
            <>
              <CaptionRow>
                <CaptionFieldSlot>
                  <Input
                    value={caption}
                    placeholder={t`Caption`}
                    aria-label={t`Photo caption`}
                    disabled={busy}
                    inputSize='sm'
                    onChange={onCaptionInput}
                  />
                </CaptionFieldSlot>
                <Button
                  type='button'
                  variant='outline'
                  disabled={busy || !captionDirty}
                  onClick={onSave}>
                  {busy && captionDirty && <Spinner />}
                  <Trans>Save caption</Trans>
                </Button>
              </CaptionRow>
              <ActionsRow>
                {confirming === null ? (
                  <>
                    <Button
                      type='button'
                      variant='outline'
                      disabled={busy}
                      title={t`Remove this photo from this Subject (the photo itself is kept)`}
                      onClick={onConfirmDetach}>
                      <Trans>Detach</Trans>
                    </Button>
                    <Button
                      type='button'
                      variant='destructive'
                      disabled={busy}
                      title={t`Detach everywhere and delete the photo`}
                      onClick={onConfirmDelete}>
                      <Trans>Delete</Trans>
                    </Button>
                  </>
                ) : (
                  <>
                    <Text color='muted' variant='caption'>
                      {confirming === 'delete'
                        ? t`Delete this photo everywhere?`
                        : t`Detach this photo from this Subject?`}
                    </Text>
                    <Button
                      type='button'
                      variant='destructive'
                      disabled={busy}
                      onClick={onConfirm}>
                      {busy && <Spinner />}
                      <Trans>Confirm</Trans>
                    </Button>
                    <Button
                      type='button'
                      variant='outline'
                      disabled={busy}
                      onClick={onCancelConfirm}>
                      <Trans>Cancel</Trans>
                    </Button>
                  </>
                )}
              </ActionsRow>
            </>
          )}
        </Body>
      </DialogContent>
    </Dialog>
  )
}
